import { useState, useEffect } from "react";
import axios from "axios";
import { Leaf, LogOut, Search, RefreshCw, MessageSquare, Trash2, Send, Flame, Snowflake, CloudSun, User } from "lucide-react";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "igreen_admin_token";

const statusStyles = {
  QUENTE: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", icon: Flame },
  MORNO: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200", icon: CloudSun },
  FRIO: { bg: "bg-sky-100", text: "text-sky-700", border: "border-sky-200", icon: Snowflake },
  NOVO: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200", icon: User },
};

function StatusBadge({ status }) {
  const cfg = statusStyles[status] || statusStyles.NOVO;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <Icon className="w-3 h-3" /> {status || "NOVO"}
    </span>
  );
}

function LoginScreen({ onLogin }) {
  const { toast } = useToast();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast({ title: "Bem-vindo!", description: "Login realizado com sucesso." });
    } catch (e) {
      toast({ title: "Erro no login", description: "Usuário ou senha incorretos." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 p-4">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8">
        <div className="flex items-center justify-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-lg">
            <Leaf className="w-7 h-7 text-white" />
          </div>
        </div>
        <h1 className="text-2xl font-extrabold text-center text-slate-900">Painel Admin</h1>
        <p className="text-sm text-slate-500 text-center mt-1">iGreen Energy - Gestão de Leads</p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Usuário" className="h-11" autoFocus />
          <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Senha" type="password" className="h-11" />
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors disabled:opacity-70"
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
      </div>
      <Toaster />
    </div>
  );
}

function LeadDetail({ token, sessionId, onClose, onDeleted }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads/${sessionId}`, {
        headers: { Authorization: `Basic ${token}` },
      });
      setData(data);
    } catch (e) {
      toast({ title: "Erro", description: "Não foi possível carregar o lead." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [sessionId]);

  const deleteLead = async () => {
    if (!window.confirm("Excluir este lead e todo o histórico da conversa?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${sessionId}`, {
        headers: { Authorization: `Basic ${token}` },
      });
      toast({ title: "Excluído" });
      onDeleted();
    } catch (e) {
      toast({ title: "Erro ao excluir" });
    }
  };

  const lead = data?.lead || {};
  const messages = data?.messages || [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{lead.name || "Lead sem nome"}</h2>
            <p className="text-sm text-slate-500">Sessão: {sessionId}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={deleteLead} className="p-2 rounded-lg text-red-600 hover:bg-red-50" title="Excluir lead">
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold">
              Fechar
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500">Carregando...</div>
        ) : (
          <div className="flex-1 overflow-auto grid md:grid-cols-3 gap-0">
            <div className="md:col-span-1 border-r border-slate-200 p-5 space-y-3 bg-slate-50">
              <div className="flex items-center gap-2">
                <StatusBadge status={lead.status} />
              </div>
              <InfoRow label="Nome" value={lead.name} />
              <InfoRow label="Contato" value={lead.contact} />
              <InfoRow label="Email" value={lead.email} />
              <InfoRow label="Perfil" value={lead.perfil} />
              <InfoRow label="Cidade" value={lead.cidade} />
              <InfoRow label="Distribuidora" value={lead.distribuidora} />
              <InfoRow label="Valor Conta" value={lead.valor_conta} />
              <InfoRow label="Grupo" value={lead.grupo} />
              <InfoRow label="Origem" value={lead.source} />
              <InfoRow label="Criado em" value={lead.created_at ? new Date(lead.created_at).toLocaleString("pt-BR") : ""} />
              <InfoRow label="Notificado" value={lead.notified ? "Sim" : "Não"} />
              {lead.contact && (
                <a
                  href={`https://wa.me/${(lead.contact || "").replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600"
                >
                  <Send className="w-4 h-4" /> Chamar no WhatsApp
                </a>
              )}
            </div>

            <div className="md:col-span-2 p-5 overflow-y-auto max-h-[70vh]">
              <div className="text-sm text-slate-500 mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Histórico da conversa com SOL ({messages.length} mensagens)
              </div>
              <div className="space-y-3">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-emerald-500 text-white rounded-br-sm"
                          : "bg-slate-100 text-slate-800 rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                      <div className={`text-[10px] mt-1 ${m.role === "user" ? "text-emerald-100" : "text-slate-500"}`}>
                        {m.ts ? new Date(m.ts).toLocaleString("pt-BR") : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wide">{label}</div>
      <div className="text-sm text-slate-900 font-medium mt-0.5">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers: { Authorization: `Basic ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Basic ${token}` } }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        onLogout();
      } else {
        toast({ title: "Erro ao carregar", description: "Verifique sua conexão." });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const testNotify = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/test-notify`, {}, { headers: { Authorization: `Basic ${token}` } });
      if (!data.configured) {
        toast({ title: "CallMeBot não configurado", description: "Adicione CALLMEBOT_API_KEY no .env do backend." });
      } else if (data.success) {
        toast({ title: "Enviado!", description: "Verifique o WhatsApp do Rodney." });
      } else {
        toast({ title: "Falha no envio", description: "Verifique a API key." });
      }
    } catch (e) {
      toast({ title: "Erro" });
    }
  };

  const filtered = leads.filter((l) => {
    if (filter !== "ALL" && (l.status || "NOVO") !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      const hay = `${l.name || ""} ${l.contact || ""} ${l.email || ""} ${l.cidade || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-slate-900 text-sm">Painel Admin</div>
              <div className="text-xs text-slate-500">iGreen Energy</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={testNotify} className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 border border-slate-200 hover:bg-slate-50">
              <Send className="w-4 h-4" /> Testar WhatsApp
            </button>
            <button onClick={load} className="p-2 rounded-lg text-slate-700 border border-slate-200 hover:bg-slate-50" title="Atualizar">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white bg-slate-800 hover:bg-slate-900">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={stats.total || 0} color="slate" />
          <StatCard label="QUENTE" value={stats.quente || 0} color="red" />
          <StatCard label="MORNO" value={stats.morno || 0} color="amber" />
          <StatCard label="FRIO" value={stats.frio || 0} color="sky" />
          <StatCard label="Mensagens" value={stats.total_messages || 0} color="emerald" />
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col md:flex-row items-stretch md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nome, contato, email, cidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-emerald-400 text-sm"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["ALL", "QUENTE", "MORNO", "FRIO", "NOVO"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                  filter === f
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {f === "ALL" ? "Todos" : f}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Carregando leads...</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Nenhum lead encontrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Nome</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Contato</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Cidade</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Distribuidora</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Conta</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Msgs</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Atualizado</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.session_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedSession(l.session_id)}>
                      <td className="px-4 py-3 text-slate-900 font-medium">{l.name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-slate-700">{l.contact || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                      <td className="px-4 py-3 text-slate-700">{l.cidade || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-slate-700">{l.distribuidora || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-slate-700">{l.valor_conta || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-slate-700">{l.message_count || 0}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {l.updated_at ? new Date(l.updated_at).toLocaleString("pt-BR") : ""}
                      </td>
                      <td className="px-4 py-3">
                        <button className="text-emerald-600 font-semibold hover:underline text-xs" onClick={(e) => { e.stopPropagation(); setSelectedSession(l.session_id); }}>Ver</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {selectedSession && (
        <LeadDetail
          token={token}
          sessionId={selectedSession}
          onClose={() => setSelectedSession(null)}
          onDeleted={() => { setSelectedSession(null); load(); }}
        />
      )}
      <Toaster />
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    slate: "text-slate-600",
    red: "text-red-600",
    amber: "text-amber-600",
    sky: "text-sky-600",
    emerald: "text-emerald-600",
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="text-xs uppercase font-bold text-slate-500 tracking-wide">{label}</div>
      <div className={`text-3xl font-extrabold mt-1 ${colors[color] || colors.slate}`}>{value}</div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return <LoginScreen onLogin={setToken} />;
  }

  return <Dashboard token={token} onLogout={logout} />;
}
